'use client';

// Inspired by react-hot-toast library
import * as React from 'react';
import axios, { type AxiosError } from 'axios';

import { ERROR_LOCATIONS, parseApiError } from '~/components/Prescriptions/apiErrors';
import type { ToastActionElement, ToastProps } from '~/components/ui/toast';

const TOAST_LIMIT = 2;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType['ADD_TOAST'];
      toast: ToasterToast;
    }
  | {
      type: ActionType['UPDATE_TOAST'];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType['DISMISS_TOAST'];
      toastId?: ToasterToast['id'];
    }
  | {
      type: ActionType['REMOVE_TOAST'];
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type ToastInput = Omit<ToasterToast, 'id' | 'description'> & {
  description?: React.ReactNode | ((dismiss: () => void) => React.ReactNode);
};

function toast({ description, ...props }: ToastInput) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });
  const resolvedDescription =
    typeof description === 'function' ? description(dismiss) : description;

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      description: resolvedDescription,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

function errorToast({
  error,
  title,
  message,
}: {
  error: unknown;
  title: string;
  message?: string;
}) {
  console.error('[ErrorToast]', error);
  return toast({
    variant: 'destructive',
    title: title,
    description: message ?? extractErrorMessage(error),
  });
}

function extractFieldErrors(data: Record<string, unknown>): string | null {
  const messages = data?.messages;
  if (!messages || typeof messages !== 'object') return null;

  const allErrors: string[] = [];
  for (const section of Object.values(messages as Record<string, unknown>)) {
    if (!section || typeof section !== 'object') continue;
    for (const fieldErrors of Object.values(section as Record<string, unknown>)) {
      if (Array.isArray(fieldErrors)) {
        allErrors.push(...fieldErrors.filter((e) => typeof e === 'string'));
      }
    }
  }

  return allErrors.length > 0 ? allErrors.join('. ') : null;
}

function messageFromWebargs(data: unknown): string | null {
  if (data == null || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const hasLocation = ERROR_LOCATIONS.some((location) => {
    const nested = record[location];
    return nested != null && typeof nested === 'object';
  });
  if (!hasLocation) return null;

  const { fields, general } = parseApiError(data);
  const text = [general, ...Object.values(fields)].filter(Boolean).join(' ');
  return text || null;
}

function messageFromErrorData(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const fieldErrors = extractFieldErrors(record);
  if (fieldErrors) return fieldErrors;
  if (record.type === 'ValidationError')
    return 'ValidationError: There was a problem with the data provided to our system.';
  if ('message' in record) return `${record.message}`;
  if (typeof record.error === 'string') return record.error;
  return messageFromWebargs(data);
}

function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return messageFromErrorData(axiosError.response?.data) ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return messageFromErrorData(error) ?? 'An unexpected error occurred';
}

function isDuplicateError(error: unknown): boolean {
  return (error as { type?: string } | null)?.type === 'Conflict';
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    errorToast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast, extractErrorMessage, isDuplicateError };
